import React from 'react';

// Standardized form field interfaces
export interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string | string[];
  tooltip?: string;
  children: React.ReactNode;
  className?: string;
}

export interface FormGridProps {
  columns?: 1 | 2 | 3 | 4;
  gap?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

export interface FormSectionProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

export interface FormActionsProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

// Standardized Form Field Component
export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  tooltip,
  children,
  className = ''
}) => {
  const hasError = error && (Array.isArray(error) ? error.length > 0 : error.length > 0);

  return (
    <div className={`form-field ${className}`}>
      <label className="form-field-label">
        {label}
        {required && <span className="form-field-required">*</span>}
        {tooltip && (
          <span className="form-field-tooltip" title={tooltip}>
            ℹ️
          </span>
        )}
      </label>
      <div className={`form-field-input ${hasError ? 'form-field-error' : ''}`}>
        {children}
      </div>
      {hasError && (
        <div className="form-field-error-message">
          {Array.isArray(error) ? (
            error.map((err, index) => (
              <div key={index} className="form-field-error-item">
                {err}
              </div>
            ))
          ) : (
            <div className="form-field-error-item">{error}</div>
          )}
        </div>
      )}
    </div>
  );
};

// Standardized Form Grid Component
export const FormGrid: React.FC<FormGridProps> = ({
  columns = 2,
  gap = 'md',
  children,
  className = ''
}) => {
  const gridClass = `form-grid form-grid-${columns} form-grid-gap-${gap} ${className}`;
  
  return (
    <div className={gridClass}>
      {children}
    </div>
  );
};

// Standardized Form Section Component
export const FormSection: React.FC<FormSectionProps> = ({
  title,
  subtitle,
  children,
  className = ''
}) => {
  return (
    <div className={`form-section ${className}`}>
      {(title || subtitle) && (
        <div className="form-section-header">
          {title && <h3 className="form-section-title">{title}</h3>}
          {subtitle && <p className="form-section-subtitle">{subtitle}</p>}
        </div>
      )}
      <div className="form-section-content">
        {children}
      </div>
    </div>
  );
};

// Standardized Form Actions Component
export const FormActions: React.FC<FormActionsProps> = ({
  children,
  align = 'right',
  className = ''
}) => {
  return (
    <div className={`form-actions form-actions-${align} ${className}`}>
      {children}
    </div>
  );
};

// Standardized Input Components
export interface StandardInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export interface StandardSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  children: React.ReactNode;
}

export interface StandardTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const StandardInput: React.FC<StandardInputProps> = ({
  error = false,
  className = '',
  ...props
}) => {
  return (
    <input
      className={`form-input-standard ${error ? 'form-input-error' : ''} ${className}`}
      {...props}
    />
  );
};

export const StandardSelect: React.FC<StandardSelectProps> = ({
  error = false,
  className = '',
  children,
  ...props
}) => {
  return (
    <select
      className={`form-select-standard ${error ? 'form-select-error' : ''} ${className}`}
      {...props}
    >
      {children}
    </select>
  );
};

export const StandardTextarea: React.FC<StandardTextareaProps> = ({
  error = false,
  className = '',
  ...props
}) => {
  return (
    <textarea
      className={`form-textarea-standard ${error ? 'form-textarea-error' : ''} ${className}`}
      {...props}
    />
  );
};

// Quantity Input with Auto-calculation
export interface QuantityInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  placeholder = "0",
  error = false,
  disabled = false,
  tooltip
}) => {
  return (
    <div className="quantity-input">
      <label className="quantity-input-label">
        {label}
        {tooltip && (
          <span className="form-field-tooltip" title={tooltip}>
            ℹ️
          </span>
        )}
      </label>
      <input
        type="number"
        className={`quantity-input-field ${error ? 'form-input-error' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

// Difference Calculator Component
export interface DifferenceDisplayProps {
  current: number;
  adjusted: number;
  label?: string;
  showPercentage?: boolean;
}

export const DifferenceDisplay: React.FC<DifferenceDisplayProps> = ({
  current,
  adjusted,
  label = "Difference",
  showPercentage = false
}) => {
  const difference = adjusted - current;
  const percentage = current > 0 ? ((difference / current) * 100) : 0;
  
  const getColorClass = () => {
    if (difference > 0) return 'difference-positive';
    if (difference < 0) return 'difference-negative';
    return 'difference-neutral';
  };

  return (
    <div className="difference-display">
      <label className="difference-display-label">{label}</label>
      <div className={`difference-display-value ${getColorClass()}`}>
        <span className="difference-number">
          {difference > 0 ? '+' : ''}{difference}
        </span>
        {showPercentage && current > 0 && (
          <span className="difference-percentage">
            ({percentage > 0 ? '+' : ''}{percentage.toFixed(1)}%)
          </span>
        )}
      </div>
    </div>
  );
};

// Reason Selector Component
export interface ReasonSelectorProps {
  value: string;
  onChange: (value: string) => void;
  reasons: Array<{ value: string; label: string; description?: string }>;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
}

export const ReasonSelector: React.FC<ReasonSelectorProps> = ({
  value,
  onChange,
  reasons,
  placeholder = "Select reason...",
  error = false,
  required = false
}) => {
  return (
    <div className="reason-selector">
      <select
        className={`reason-selector-field ${error ? 'form-select-error' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      >
        <option value="">{placeholder}</option>
        {reasons.map((reason) => (
          <option key={reason.value} value={reason.value} title={reason.description}>
            {reason.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Item List Component for dynamic forms
export interface ItemListProps<T> {
  items: T[];
  onAddItem: () => void;
  onRemoveItem: (index: number) => void;
  onUpdateItem: (index: number, field: string, value: any) => void;
  renderItem: (item: T, index: number, onUpdate: (field: string, value: any) => void, onRemove: () => void) => React.ReactNode;
  addButtonText?: string;
  emptyMessage?: string;
  title?: string;
  error?: string | string[];
}

export function ItemList<T>({
  items,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
  renderItem,
  addButtonText = "Add Item",
  emptyMessage = "No items added yet",
  title,
  error
}: ItemListProps<T>) {
  const hasError = error && (Array.isArray(error) ? error.length > 0 : error.length > 0);

  return (
    <div className="item-list">
      <div className="item-list-header">
        {title && <h4 className="item-list-title">{title}</h4>}
        <button
          type="button"
          className="btn btn-secondary item-list-add-btn"
          onClick={onAddItem}
        >
          <span className="btn-icon">➕</span>
          {addButtonText}
        </button>
      </div>
      
      {hasError && (
        <div className="form-field-error-message">
          {Array.isArray(error) ? (
            error.map((err, index) => (
              <div key={index} className="form-field-error-item">
                {err}
              </div>
            ))
          ) : (
            <div className="form-field-error-item">{error}</div>
          )}
        </div>
      )}

      <div className="item-list-content">
        {items.length === 0 ? (
          <div className="item-list-empty">
            {emptyMessage}
          </div>
        ) : (
          items.map((item, index) => (
            <div key={index} className="item-list-item">
              {renderItem(
                item,
                index,
                (field, value) => onUpdateItem(index, field, value),
                () => onRemoveItem(index)
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Validation Summary Component
export interface ValidationSummaryProps {
  errors: Record<string, string | string[]>;
  title?: string;
  className?: string;
}

export const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  errors,
  title = "Please correct the following errors:",
  className = ''
}) => {
  const errorEntries = Object.entries(errors).filter(([_, error]) => 
    error && (Array.isArray(error) ? error.length > 0 : error.length > 0)
  );

  if (errorEntries.length === 0) return null;

  return (
    <div className={`form-validation-summary ${className}`}>
      <h4>{title}</h4>
      <ul>
        {errorEntries.map(([field, error]) => (
          <li key={field}>
            <strong>{field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>{' '}
            {Array.isArray(error) ? error.join(', ') : error}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Enhanced Form Field with better error handling
export interface EnhancedFormFieldProps extends FormFieldProps {
  error?: string | string[];
  touched?: boolean;
  onBlur?: () => void;
  helpText?: string;
}

export const EnhancedFormField: React.FC<EnhancedFormFieldProps> = ({
  label,
  required = false,
  error,
  touched = false,
  tooltip,
  helpText,
  onBlur,
  children,
  className = ''
}) => {
  const hasError = error && (Array.isArray(error) ? error.length > 0 : error.length > 0);
  const showError = hasError && touched;

  return (
    <div className={`form-field enhanced-form-field ${className} ${showError ? 'has-error' : ''}`}>
      <label className="form-field-label">
        {label}
        {required && <span className="form-field-required">*</span>}
        {tooltip && (
          <span className="form-field-tooltip" title={tooltip}>
            ℹ️
          </span>
        )}
      </label>
      <div className={`form-field-input ${showError ? 'form-field-error' : ''}`} onBlur={onBlur}>
        {children}
      </div>
      {helpText && !showError && (
        <div className="form-field-help-text">
          {helpText}
        </div>
      )}
      {showError && (
        <div className="form-field-error-message">
          {Array.isArray(error) ? (
            error.map((err, index) => (
              <div key={index} className="form-field-error-item">
                <span className="error-icon">⚠️</span>
                {err}
              </div>
            ))
          ) : (
            <div className="form-field-error-item">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Form validation hook
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
}

export interface FieldValidation {
  [fieldName: string]: ValidationRule;
}

export function useFormValidation<T extends Record<string, any>>(
  initialValues: T,
  validationRules: FieldValidation
) {
  const [values, setValues] = React.useState<T>(initialValues);
  const [errors, setErrors] = React.useState<Record<string, string[]>>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const validateField = (fieldName: string, value: any): string[] => {
    const rules = validationRules[fieldName];
    if (!rules) return [];

    const fieldErrors: string[] = [];

    if (rules.required && (!value || (typeof value === 'string' && !value.trim()))) {
      fieldErrors.push(`${fieldName.replace('_', ' ')} is required`);
    }

    if (value && typeof value === 'string') {
      if (rules.minLength && value.length < rules.minLength) {
        fieldErrors.push(`Must be at least ${rules.minLength} characters`);
      }
      if (rules.maxLength && value.length > rules.maxLength) {
        fieldErrors.push(`Must be no more than ${rules.maxLength} characters`);
      }
      if (rules.pattern && !rules.pattern.test(value)) {
        fieldErrors.push(`Invalid format`);
      }
    }

    if (value && typeof value === 'number') {
      if (rules.min !== undefined && value < rules.min) {
        fieldErrors.push(`Must be at least ${rules.min}`);
      }
      if (rules.max !== undefined && value > rules.max) {
        fieldErrors.push(`Must be no more than ${rules.max}`);
      }
    }

    if (rules.custom) {
      const customError = rules.custom(value);
      if (customError) {
        fieldErrors.push(customError);
      }
    }

    return fieldErrors;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string[]> = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const fieldErrors = validateField(fieldName, values[fieldName]);
      if (fieldErrors.length > 0) {
        newErrors[fieldName] = fieldErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const setValue = (fieldName: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [fieldName]: value }));
    
    // Validate field on change if it was previously touched
    if (touched[fieldName as string]) {
      const fieldErrors = validateField(fieldName as string, value);
      setErrors(prev => ({
        ...prev,
        [fieldName]: fieldErrors
      }));
    }
  };

  const setFieldTouched = (fieldName: keyof T) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    // Validate field when touched
    const fieldErrors = validateField(fieldName as string, values[fieldName]);
    setErrors(prev => ({
      ...prev,
      [fieldName]: fieldErrors
    }));
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  };

  return {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    validateForm,
    resetForm,
    isValid: Object.keys(errors).length === 0
  };
}

// Common adjustment reasons
export const ADJUSTMENT_REASONS = [
  { value: 'physical_count', label: 'Physical Count', description: 'Adjustment based on physical inventory count' },
  { value: 'kitting', label: 'Kitting', description: 'Kit/bundle assembly from components' },
  { value: 'damage', label: 'Damage', description: 'Items damaged and unusable' },
  { value: 'theft', label: 'Theft', description: 'Items stolen or missing' },
  { value: 'correction', label: 'Correction', description: 'Correction of previous entry error' },
  { value: 'expired', label: 'Expired', description: 'Items past expiration date' },
  { value: 'return', label: 'Return', description: 'Items returned by customer' },
  { value: 'transfer_error', label: 'Transfer Error', description: 'Error in transfer recording' },
  { value: 'system_error', label: 'System Error', description: 'System or data entry error' },
  { value: 'other', label: 'Other', description: 'Other reason not listed above' }
];